import type { QuickRaceEngine, RaceParticipant } from "../core/race";
import { VirtualGear } from "./virtualGear";
import { audioMix, preferredFocusId, shepardVoiceMix } from "./audioMix";

type MusicScene = "menu" | "race" | "race3d" | "forever" | "silent";
type CountdownCue = "3" | "2" | "1" | "GO";
type AudioContextConstructor = typeof AudioContext;

interface MusicManifest {
  menu: string[];
  racing: string[];
  racing3d?: string[];
  forever?: string[];
}

const SETTINGS_KEY = "neurodrive-audio-v1";

export function untriedMusicChoice(
  available: string[],
  attempted: ReadonlySet<string>,
  randomValue = Math.random(),
): string | null {
  const candidates = available.filter((name) => !attempted.has(name));
  if (candidates.length === 0) return null;
  const index = Math.min(candidates.length - 1, Math.floor(Math.max(0, randomValue) * candidates.length));
  return candidates[index] ?? null;
}

function storedMuted(): boolean {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? "{}").muted === true;
  } catch {
    return false;
  }
}

export class RaceAudioController {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private engine: OscillatorNode[] = [];
  private engineVoiceGains: GainNode[] = [];
  private engineGain: GainNode | null = null;
  private pack: OscillatorNode | null = null;
  private packGain: GainNode | null = null;
  private windGain: GainNode | null = null;
  private skidGain: GainNode | null = null;
  private mutedState = storedMuted();
  private scene: MusicScene = "menu";
  private music: HTMLAudioElement | null = null;
  private manifest: MusicManifest | null = null;
  private manifestPromise: Promise<void> | null = null;
  private musicAttempts = new Set<string>();
  private focusId: string | null = null;
  private pendingFocusId: string | null = null;
  private pendingFocusSince = 0;
  private impactSignature = "";
  private rabbitCursor = 0;
  private landingCounts = new Map<string, number>();
  private lastBumpAt = -Infinity;
  private lastBunnyAt = -Infinity;
  private lastRailAt = -Infinity;
  private trackCollisionCount = 0;
  private shepardPhase = 0;
  private readonly gearbox = new VirtualGear();
  private driveRabbits = 0;
  private driveImpacts = 0;
  private lastRaceUpdateMs: number | null = null;
  private stateListener: (() => void) | null = null;

  get muted(): boolean { return this.mutedState; }
  get awaitingActivation(): boolean {
    return !this.mutedState && this.scene !== "silent" && (!this.music || this.music.paused);
  }

  setStateListener(listener: () => void): void { this.stateListener = listener; }

  async prepare(): Promise<void> {
    await this.loadManifest();
  }

  async activate(): Promise<void> {
    const AudioCtor = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext;
    if (AudioCtor) {
      try {
        if (!this.context) this.createGraph(new AudioCtor());
        if (this.context?.state === "suspended") void this.context.resume().catch(() => undefined);
      } catch {
        // HTMLAudio-musikken kan stadig virke uden Web Audio-effekterne.
      }
    }
    void this.loadManifest();
    await this.syncMusic();
  }

  toggleMuted(): void {
    this.mutedState = !this.mutedState;
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify({ muted: this.mutedState })); } catch { /* private mode */ }
    if (this.master && this.context) this.master.gain.setTargetAtTime(this.mutedState ? 0 : 0.78, this.context.currentTime, 0.04);
    if (this.music) this.music.volume = this.mutedState ? 0 : this.musicVolume();
    if (!this.mutedState) void this.activate();
    this.notifyState();
  }

  setScene(scene: MusicScene): void {
    if (this.scene === scene) return;
    this.scene = scene;
    this.musicAttempts.clear();
    void this.syncMusic();
  }

  beginRace(engine: QuickRaceEngine): void {
    this.focusId = preferredFocusId(engine.participants, engine.results[0]?.participantId ?? null);
    this.pendingFocusId = null;
    this.impactSignature = this.latestImpactSignature(engine);
    this.rabbitCursor = engine.rabbitWorld.decals.length;
    this.landingCounts = new Map(engine.participants.map((participant) => [participant.selection.slotId, participant.cleanLandings]));
    this.trackCollisionCount = engine.participants.reduce((sum, participant) => sum + participant.trackCollisions, 0);
    this.lastRaceUpdateMs = null;
    this.gearbox.reset();
    this.setScene("race");
  }

  updateRace(engine: QuickRaceEngine, active: boolean, nowMs: number): void {
    if (!this.context) return;
    this.updateFocus(engine.participants, engine.results[0]?.participantId ?? null, nowMs);
    const mix = audioMix(engine.participants, this.focusId, active && !this.mutedState);
    const now = this.context.currentTime;
    const focus = engine.participants.find((participant) => participant.selection.slotId === this.focusId);
    const dt = this.lastRaceUpdateMs === null ? 0 : Math.min(0.1, Math.max(0, (nowMs - this.lastRaceUpdateMs) / 1000));
    this.lastRaceUpdateMs = nowMs;
    const gear = this.gearbox.step(focus ? Math.abs(focus.car.speed) / Math.max(1, focus.vehicle.physics.max_forward_speed) : 0, focus?.control.throttleBrake ?? 0, dt, active);
    this.shepardPhase = gear.phase;
    shepardVoiceMix(this.shepardPhase).forEach((voice, index) => {
      this.engine[index]?.frequency.setValueAtTime(voice.frequency, now);
      this.engineVoiceGains[index]?.gain.setTargetAtTime(voice.gain, now, 0.035);
    });
    this.pack?.frequency.setTargetAtTime(mix.packFrequency, now, 0.16);
    this.engineGain?.gain.setTargetAtTime(mix.engineGain * gear.gain, now, 0.06);
    this.packGain?.gain.setTargetAtTime(mix.packGain, now, 0.18);
    this.windGain?.gain.setTargetAtTime(mix.windGain, now, 0.08);
    this.skidGain?.gain.setTargetAtTime(mix.skidGain, now, 0.08);
    if (!active) return;
    this.consumeEvents(engine, nowMs);
  }

  beginDrive(): void {
    this.gearbox.reset(); this.driveRabbits = 0; this.driveImpacts = 0;
    this.setScene("forever");
  }

  updateDrive(speed: number, maximum: number, command: number, slipping: boolean, active: boolean, dt: number, rabbits: number, impacts: number): void {
    if (!this.context) return;
    const now = this.context.currentTime, ratio = Math.min(1, Math.abs(speed) / Math.max(1, maximum));
    const gear = this.gearbox.step(ratio, command, dt, active);
    shepardVoiceMix(gear.phase).forEach((voice, index) => {
      this.engine[index]?.frequency.setTargetAtTime(voice.frequency, now, .012);
      this.engineVoiceGains[index]?.gain.setTargetAtTime(voice.gain, now, .025);
    });
    const sounding = active && !this.mutedState;
    this.engineGain?.gain.setTargetAtTime(sounding ? (.065 + .065 * ratio + .022 * Math.max(0, command)) * gear.gain : 0, now, .035);
    this.windGain?.gain.setTargetAtTime(sounding ? ratio * .014 : 0, now, .08);
    this.skidGain?.gain.setTargetAtTime(sounding && slipping ? .018 : 0, now, .06);
    this.packGain?.gain.setTargetAtTime(0, now, .1);
    if (active && rabbits > this.driveRabbits) this.bunny();
    if (active && impacts > this.driveImpacts && performance.now() - this.lastBumpAt > 180) { this.bump(); this.lastBumpAt = performance.now(); }
    this.driveRabbits = rabbits; this.driveImpacts = impacts;
  }

  countdown(cue: CountdownCue): void {
    if (!this.context || this.mutedState) return;
    this.tone(cue === "GO" ? 620 : 390, cue === "GO" ? 0.17 : 0.09, cue === "GO" ? 0.055 : 0.035, cue === "GO" ? 820 : 390);
  }

  silenceRace(): void {
    if (!this.context) return;
    const now = this.context.currentTime;
    for (const gain of [this.engineGain, this.packGain, this.windGain, this.skidGain]) gain?.gain.setTargetAtTime(0, now, 0.06);
  }

  private createGraph(context: AudioContext): void {
    this.context = context;
    this.master = context.createGain();
    this.master.gain.value = this.mutedState ? 0 : 0.78;
    this.master.connect(context.destination);

    const engineFilter = context.createBiquadFilter();
    engineFilter.type = "lowpass"; engineFilter.frequency.value = 330; engineFilter.Q.value = 0.45;
    const engineWave = context.createPeriodicWave(
      new Float32Array([0, 0, 0, 0, 0, 0]),
      new Float32Array([0, 1, 0.24, 0.31, 0.1, 0.15]),
    );
    this.engineGain = context.createGain(); this.engineGain.gain.value = 0;
    engineFilter.connect(this.engineGain).connect(this.master);
    for (let index = 0; index < 4; index += 1) {
      const oscillator = context.createOscillator();
      const voiceGain = context.createGain(); voiceGain.gain.value = 0;
      oscillator.setPeriodicWave(engineWave);
      oscillator.connect(voiceGain).connect(engineFilter);
      oscillator.start();
      this.engine.push(oscillator);
      this.engineVoiceGains.push(voiceGain);
    }

    this.pack = context.createOscillator(); this.pack.type = "sine";
    const packFilter = context.createBiquadFilter(); packFilter.type = "lowpass"; packFilter.frequency.value = 260;
    this.packGain = context.createGain(); this.packGain.gain.value = 0;
    this.pack.connect(packFilter).connect(this.packGain).connect(this.master);
    this.pack.start();

    const noise = context.createBuffer(1, context.sampleRate * 2, context.sampleRate);
    const samples = noise.getChannelData(0);
    let previous = 0;
    for (let index = 0; index < samples.length; index += 1) {
      previous = previous * 0.82 + (Math.random() * 2 - 1) * 0.18;
      samples[index] = previous;
    }
    const wind = context.createBufferSource(); wind.buffer = noise; wind.loop = true;
    const windFilter = context.createBiquadFilter(); windFilter.type = "bandpass"; windFilter.frequency.value = 1050; windFilter.Q.value = 0.6;
    this.windGain = context.createGain(); this.windGain.gain.value = 0;
    wind.connect(windFilter).connect(this.windGain).connect(this.master); wind.start();
    const skid = context.createBufferSource(); skid.buffer = noise; skid.loop = true;
    const skidFilter = context.createBiquadFilter(); skidFilter.type = "highpass"; skidFilter.frequency.value = 1500;
    this.skidGain = context.createGain(); this.skidGain.gain.value = 0;
    skid.connect(skidFilter).connect(this.skidGain).connect(this.master); skid.start();
  }

  private updateFocus(participants: RaceParticipant[], leaderId: string | null, nowMs: number): void {
    const candidate = preferredFocusId(participants, leaderId);
    if (participants.some((participant) => participant.driver === null)) { this.focusId = candidate; return; }
    if (candidate === this.focusId) { this.pendingFocusId = null; return; }
    if (candidate !== this.pendingFocusId) { this.pendingFocusId = candidate; this.pendingFocusSince = nowMs; return; }
    if (nowMs - this.pendingFocusSince >= 900) { this.focusId = candidate; this.pendingFocusId = null; }
  }

  private consumeEvents(engine: QuickRaceEngine, nowMs: number): void {
    const impactSignature = this.latestImpactSignature(engine);
    if (impactSignature && impactSignature !== this.impactSignature && nowMs - this.lastBumpAt >= 110) {
      this.bump(); this.lastBumpAt = nowMs;
      this.shepardPhase = (this.shepardPhase - 0.10 + 1) % 1;
    }
    this.impactSignature = impactSignature;
    if (engine.rabbitWorld.decals.length < this.rabbitCursor) this.rabbitCursor = 0;
    const rabbits = engine.rabbitWorld.decals.length - this.rabbitCursor;
    this.rabbitCursor = engine.rabbitWorld.decals.length;
    if (rabbits > 0 && nowMs - this.lastBunnyAt >= 240) {
      this.bunny(); this.lastBunnyAt = nowMs;
    }
    const trackCollisionCount = engine.participants.reduce((sum, participant) => sum + participant.trackCollisions, 0);
    if (trackCollisionCount > this.trackCollisionCount && nowMs - this.lastRailAt >= 180) {
      this.guardrail(); this.lastRailAt = nowMs;
    }
    this.trackCollisionCount = trackCollisionCount;
    for (const participant of engine.participants) {
      const previous = this.landingCounts.get(participant.selection.slotId) ?? participant.cleanLandings;
      if (participant.cleanLandings > previous) this.landing();
      this.landingCounts.set(participant.selection.slotId, participant.cleanLandings);
    }
  }

  private latestImpactSignature(engine: QuickRaceEngine): string {
    const event = engine.impactEvents.at(-1);
    return event ? `${event.elapsedSeconds}:${event.participantIds.join(":")}` : "";
  }

  private tone(start: number, duration: number, gainValue: number, end = start): void {
    if (!this.context || !this.master) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator(); oscillator.type = "sine";
    const gain = this.context.createGain(); gain.gain.setValueAtTime(0.0001, now); gain.gain.exponentialRampToValueAtTime(gainValue, now + 0.012); gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.frequency.setValueAtTime(start, now); oscillator.frequency.exponentialRampToValueAtTime(end, now + duration);
    oscillator.connect(gain).connect(this.master); oscillator.start(now); oscillator.stop(now + duration + 0.02);
  }

  private bump(): void {
    this.tone(104, 0.14, 0.070, 46);
    window.setTimeout(() => this.tone(335, 0.12, 0.038, 112), 18);
    window.setTimeout(() => this.tone(540, 0.11, 0.024, 780), 82);
    window.setTimeout(() => this.tone(190, 0.08, 0.018, 285), 140);
  }
  private landing(): void { this.tone(82, 0.12, 0.045, 48); }
  private bunny(): void {
    this.tone(790, 0.10, 0.025, 430);
    window.setTimeout(() => this.tone(205, 0.13, 0.023, 92), 35);
    window.setTimeout(() => this.tone(315, 0.08, 0.014, 150), 90);
  }
  private guardrail(): void {
    this.tone(285, 0.16, 0.032, 92);
    window.setTimeout(() => this.tone(720, 0.13, 0.019, 245), 14);
    window.setTimeout(() => this.tone(940, 0.11, 0.011, 410), 55);
  }

  private loadManifest(): Promise<void> {
    if (this.manifest) return Promise.resolve();
    if (this.manifestPromise) return this.manifestPromise;
    this.manifestPromise = fetch("/audio/music/manifest.json", { cache: "no-cache" })
      .then(async (response) => { this.manifest = response.ok ? await response.json() as MusicManifest : { menu: [], racing: [] }; })
      .catch(() => { this.manifest = { menu: [], racing: [] }; })
      .finally(() => { this.manifestPromise = null; void this.syncMusic(); });
    return this.manifestPromise;
  }

  private async syncMusic(): Promise<void> {
    if (!this.manifest) return;
    const available = this.scene === "menu" ? this.manifest.menu : this.scene === "race" ? this.manifest.racing : this.scene === "race3d" ? (this.manifest.racing3d ?? []) : this.scene === "forever" ? (this.manifest.forever ?? []) : [];
    if (available.length === 0) { this.stopMusic(); this.notifyState(); return; }
    if (this.music?.dataset.scene === this.scene) {
      this.music.volume = this.mutedState ? 0 : this.musicVolume();
      if (!this.music.paused) { this.notifyState(); return; }
      try { await this.music.play(); } catch { /* første brugerhandling forsøger samme nummer igen */ }
      this.notifyState();
      return;
    }
    if (this.musicAttempts.size >= available.length) this.musicAttempts.clear();
    await this.startNextMusic(available);
  }

  private async startNextMusic(available: string[]): Promise<void> {
    const selected = untriedMusicChoice(available, this.musicAttempts);
    if (!selected) { this.stopMusic(); return; }
    this.musicAttempts.add(selected);
    const source = `/audio/music/${encodeURIComponent(selected)}`;
    this.stopMusic();
    const audio = new Audio(source);
    audio.dataset.scene = this.scene;
    audio.autoplay = true;
    audio.loop = true;
    audio.setAttribute("playsinline", "");
    audio.preload = "auto";
    audio.volume = this.mutedState ? 0 : this.musicVolume();
    audio.addEventListener("error", () => {
      if (this.music !== audio) return;
      this.music = null;
      this.notifyState();
      void this.startNextMusic(available);
    }, { once: true });
    this.music = audio;
    try { await audio.play(); } catch { /* autoplay: behold nummeret og prøv igen ved første brugerhandling */ }
    this.notifyState();
  }

  private stopMusic(): void {
    if (!this.music) return;
    this.music.pause(); this.music.currentTime = 0; this.music = null;
  }

  private notifyState(): void { this.stateListener?.(); }

  private musicVolume(): number { return (this.scene === "race" || this.scene === "race3d" || this.scene === "forever") ? 0.075 : 0.14; }
}
